const PROTOCOL_VERSION = 1;
const MAX_PROJECT_BYTES = 25 * 1024 * 1024;

const PARENT_SOURCE = 'future-innovator-app';
const SCRATCH_SOURCE = 'future-innovator-scratch';

const MESSAGE_TYPES = Object.freeze({
    exportRequest: 'future-innovator:scratch-export-request',
    exportResult: 'future-innovator:scratch-export-result',
    loadRequest: 'future-innovator:scratch-load-request',
    loadResult: 'future-innovator:scratch-load-result',
    operationError: 'future-innovator:scratch-operation-error'
});

const ERROR_CODES = Object.freeze([
    'EXPORT_FAILED',
    'LOAD_FAILED',
    'PAYLOAD_REJECTED'
]);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FORBIDDEN_KEY_PATTERN = new RegExp(`^(?:${[
    'profileId', 'schoolId', 'accessToken', 'refreshToken', 'authorization',
    'cookie', 'supabase', 'serviceRole', 'apiKey', 'secret', 'password'
].join('|')})$`, 'i');
// eslint-disable-next-line no-control-regex -- rejects untrusted control characters in project names.
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const hasExactKeys = (value, expectedKeys) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const keys = Object.keys(value);
    return keys.length === expectedKeys.length && expectedKeys.every(key => hasOwn(value, key));
};

const isUuid = value => typeof value === 'string' && UUID_PATTERN.test(value);

const isSafeProjectName = value => (
    typeof value === 'string' &&
    value === value.trim() &&
    value.length >= 1 &&
    value.length <= 100 &&
    !CONTROL_CHARACTER_PATTERN.test(value)
);

const isArrayBuffer = value => value instanceof ArrayBuffer;

const isSb3ZipArchive = value => {
    if (!isArrayBuffer(value) || value.byteLength < 4) return false;
    const header = new Uint8Array(value, 0, 4);
    return header[0] === 0x50 && header[1] === 0x4b &&
        header[2] === 0x03 && header[3] === 0x04;
};

const isValidProjectPayload = value => (
    isArrayBuffer(value) &&
    value.byteLength <= MAX_PROJECT_BYTES &&
    isSb3ZipArchive(value)
);

const containsForbiddenKeys = value => {
    if (!value || typeof value !== 'object' || isArrayBuffer(value)) return false;
    return Object.keys(value).some(key => (
        FORBIDDEN_KEY_PATTERN.test(key) || containsForbiddenKeys(value[key])
    ));
};

const validateBase = (message, source, type, expectedKeys) => {
    if (!hasExactKeys(message, expectedKeys) || containsForbiddenKeys(message)) return false;
    return message.source === source && message.type === type && message.version === PROTOCOL_VERSION &&
        isUuid(message.requestId) && isUuid(message.projectId);
};

const validateExportRequest = message => validateBase(
    message,
    PARENT_SOURCE,
    MESSAGE_TYPES.exportRequest,
    ['source', 'type', 'version', 'requestId', 'projectId']
);

const validateLoadRequestMetadata = message => validateBase(
    message,
    PARENT_SOURCE,
    MESSAGE_TYPES.loadRequest,
    ['source', 'type', 'version', 'requestId', 'projectId', 'name', 'payload']
) && isSafeProjectName(message.name);

const validateLoadRequest = message => (
    validateLoadRequestMetadata(message) && isValidProjectPayload(message.payload)
);

const validateExportResult = message => validateBase(
    message,
    SCRATCH_SOURCE,
    MESSAGE_TYPES.exportResult,
    ['source', 'type', 'version', 'requestId', 'projectId', 'payload']
) && isValidProjectPayload(message.payload);

const validateLoadResult = message => validateBase(
    message,
    SCRATCH_SOURCE,
    MESSAGE_TYPES.loadResult,
    ['source', 'type', 'version', 'requestId', 'projectId']
);

const validateOperationError = message => validateBase(
    message,
    SCRATCH_SOURCE,
    MESSAGE_TYPES.operationError,
    ['source', 'type', 'version', 'requestId', 'projectId', 'code']
) && ERROR_CODES.includes(message.code);

const createOperationError = (request, code) => {
    if (!request || !isUuid(request.requestId) || !isUuid(request.projectId) ||
        !ERROR_CODES.includes(code)) return null;

    return {
        source: SCRATCH_SOURCE,
        type: MESSAGE_TYPES.operationError,
        version: PROTOCOL_VERSION,
        requestId: request.requestId,
        projectId: request.projectId,
        code
    };
};

const createExportResult = (request, payload) => {
    const result = {
        source: SCRATCH_SOURCE,
        type: MESSAGE_TYPES.exportResult,
        version: PROTOCOL_VERSION,
        requestId: request.requestId,
        projectId: request.projectId,
        payload
    };
    return validateExportResult(result) ? result : null;
};

const createLoadResult = request => {
    const result = {
        source: SCRATCH_SOURCE,
        type: MESSAGE_TYPES.loadResult,
        version: PROTOCOL_VERSION,
        requestId: request.requestId,
        projectId: request.projectId
    };
    return validateLoadResult(result) ? result : null;
};

export {
    ERROR_CODES,
    MAX_PROJECT_BYTES,
    MESSAGE_TYPES,
    PARENT_SOURCE,
    PROTOCOL_VERSION,
    SCRATCH_SOURCE,
    createExportResult,
    createLoadResult,
    createOperationError,
    isArrayBuffer,
    isSafeProjectName,
    isUuid,
    isValidProjectPayload,
    validateExportRequest,
    validateExportResult,
    validateLoadRequest,
    validateLoadRequestMetadata,
    validateLoadResult,
    validateOperationError
};
